import { hasConsentForAnalytics } from './cookieConsent';

declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
  }
}

export const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  if (!hasConsentForAnalytics()) {
    console.log('Analytics tracking blocked: No consent', eventName);
    return;
  }

  window.gtag('event', eventName, parameters);
  console.log('GA Event:', eventName, parameters);
};

// Review-related events
export const trackReviewSubmission = (reviewData: {
  property_address: string;
  property_rating: number;
  has_images: boolean;
  review_type: string; // 'anonymous' | 'named'
  property_owner_type?: string;
  has_letting_agent: boolean;
}) => {
  trackEvent('review_submitted', {
    event_category: 'Reviews',
    property_address: reviewData.property_address,
    property_rating: reviewData.property_rating,
    has_images: reviewData.has_images,
    review_type: reviewData.review_type,
    property_owner_type: reviewData.property_owner_type || 'unknown',
    has_letting_agent: reviewData.has_letting_agent,
    value: reviewData.property_rating
  });
};

export const trackReviewModalOpen = (source: 'homepage' | 'property_page' | 'leave_review_page') => {
  trackEvent('review_modal_open', {
    event_category: 'User Engagement',
    source: source
  });
};

// Search-related events
export const trackPropertySearch = (query: string, results_count?: number) => {
  trackEvent('property_search', {
    event_category: 'Search',
    search_term: query,
    results_count: results_count || 0
  });
};

export const trackAddressSelection = (address: string, udprn: string, source: 'homepage' | 'leave_review') => {
  trackEvent('address_selected', {
    event_category: 'Navigation',
    property_address: address,
    udprn: udprn,
    source: source
  });
};

// Image-related events
export const trackImageUpload = (image_count: number, total_size_mb: number) => {
  trackEvent('images_uploaded', {
    event_category: 'Content Creation',
    image_count: image_count,
    total_size_mb: Math.round(total_size_mb * 100) / 100
  });
};

// Phone verification events
export const trackPhoneVerificationStart = (phone_country: string) => {
  trackEvent('phone_verification_start', {
    event_category: 'User Verification',
    phone_country: phone_country
  });
};

export const trackPhoneVerificationComplete = (success: boolean) => {
  trackEvent('phone_verification_complete', {
    event_category: 'User Verification',
    success: success
  });
};

// Navigation events
export const trackPageView = (page_title: string, page_location: string) => {
  trackEvent('page_view', {
    page_title: page_title,
    page_location: page_location
  });
};

// Engagement events
export const trackReviewRead = (property_address: string, review_id: string) => {
  trackEvent('review_read', {
    event_category: 'Content Engagement',
    property_address: property_address,
    review_id: review_id
  });
};

export const trackImageGalleryOpen = (property_address: string, image_count: number) => {
  trackEvent('image_gallery_open', {
    event_category: 'Content Engagement',
    property_address: property_address,
    image_count: image_count
  });
};

// FAQ engagement
export const trackFAQInteraction = (question: string, action: 'open' | 'close') => {
  trackEvent('faq_interaction', {
    event_category: 'Content Engagement',
    question: question,
    action: action
  });
};

// External link clicks
export const trackExternalLinkClick = (url: string, link_text: string) => {
  trackEvent('external_link_click', {
    event_category: 'Outbound Links',
    link_url: url,
    link_text: link_text
  });
};